import { useState, useRef, useCallback } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { FinancialEntry } from '@/modules/billing/entries/entriesTypes'
import { uploadReceipt, getReceiptUrl, deleteReceipt } from '@/modules/billing/entries/receiptsService'
import { useToast } from '@/shared/hooks/useToast'

interface Props {
  entry: FinancialEntry
  onClose: () => void
  onUpdated: (entry: FinancialEntry) => void
}

export function ReceiptModal({ entry, onClose, onUpdated }: Props) {
  const { t } = useTranslation()
  const te = (key: string, params?: Record<string, string>) => t(`billing.entries.${key}`, params)
  const toast = useToast()

  const [loading, setLoading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const hasReceipt = Boolean(entry.receipt_url)

  const handleFile = useCallback(async (file: File) => {
    setLoading(true)
    try {
      const result = await uploadReceipt(entry.unit_id, entry.id, file)
      toast.success(te('receipt.uploadSuccess'))
      onUpdated({ ...entry, receipt_url: result.path })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }, [entry, te, toast, onUpdated])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const handleView = async () => {
    if (!entry.receipt_url) return
    setLoading(true)
    try {
      const url = await getReceiptUrl(entry.receipt_url)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      toast.error('NÃ£o foi possÃ­vel abrir o comprovante.')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!entry.receipt_url) return
    if (!window.confirm(te('receipt.deleteConfirm'))) return
    setLoading(true)
    try {
      await deleteReceipt(entry.id, entry.receipt_url)
      toast.success(te('receipt.deleteSuccess'))
      onUpdated({ ...entry, receipt_url: null })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  // Header buttons for desktop/mobile
  const headerRight = (
    <div className="flex items-center gap-2">
      {/* Close button on desktop only */}
      <button
        onClick={onClose}
        className="hidden sm:flex p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-600 dark:text-slate-400"
        title="Fechar"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      {/* Checkmark button */}
      <button
        onClick={onClose}
        className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors text-emerald-600 dark:text-emerald-400"
        title="Confirmar"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </button>
    </div>
  )

  return (
    <Modal isOpen onClose={onClose} title={te('receipt.title')} size="sm" headerRight={headerRight}>
      <div className="flex flex-col gap-4">

        {/* Current receipt actions */}
        {hasReceipt && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700">
            <svg className="h-8 w-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200 truncate">
                {entry.receipt_url?.split('/').pop() ?? 'comprovante'}
              </p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                {te('receipt.title')}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={handleView}
                disabled={loading}
                className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-800 transition-colors text-emerald-700 dark:text-emerald-300"
                title={te('receipt.openFile')}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors text-rose-600 dark:text-rose-400"
                title={te('receipt.remove')}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Upload area */}
        <div
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
            ${dragOver
              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
              : 'border-slate-300 dark:border-slate-600 hover:border-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
            }
            ${loading ? 'pointer-events-none opacity-60' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {loading ? (
            <div className="flex flex-col items-center gap-2">
              <svg className="h-8 w-8 text-indigo-500 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <p className="text-sm text-slate-500 dark:text-slate-400">{te('receipt.uploading')}</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <svg className="h-10 w-10 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {hasReceipt ? 'Substituir arquivo' : te('receipt.dropzone')}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500">{te('receipt.hint')}</p>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
          className="hidden"
          onChange={handleInputChange}
        />
      </div>
    </Modal>
  )
}
